// ============================================================
// CLASSIFICATION OFFLINE DES OPÉRATIONS
// ============================================================
// Catégorise chaque type de module en trois tiers :
//   FULL_OFFLINE   → queue + résolution de conflits (données métier)
//   READ_ONLY      → cache lecture seul, écriture bloquée offline
//   ONLINE_ONLY    → désactivé proprement hors-ligne
// ============================================================

export type OfflineTier = 'FULL_OFFLINE' | 'READ_ONLY_OFFLINE' | 'ONLINE_ONLY';

interface OperationCategory {
  tier: OfflineTier;
  reason?: string;
  /** Si ONLINE_ONLY, message à afficher à l'utilisateur quand il est hors-ligne */
  offlineBlockMessage?: string;
}

// ===================== CLASSIFICATION PAR MODULE =====================

const MODULE_CATEGORIES: Record<string, OperationCategory> = {
  // ========== FULL OFFLINE ==========
  // Données métier critiques : créées/modifiées hors-ligne puis synchronisées
  rendez_vous: {
    tier: 'FULL_OFFLINE',
    reason: 'Les rendez-vous peuvent être créés et modifiés hors-ligne, avec détection de conflits de créneaux.',
  },
  consultations: {
    tier: 'FULL_OFFLINE',
    reason: 'Les consultations doivent pouvoir être saisies hors-ligne pour être synchronisées ultérieurement.',
  },
  prescriptions: {
    tier: 'FULL_OFFLINE',
    reason: 'Les prescriptions créées hors-ligne sont mises en file d\'attente et synchronisées au retour réseau.',
  },
  dossiers_roses: {
    tier: 'FULL_OFFLINE',
    reason: 'Les dossiers patients (dossier rose) sont modifiables hors-ligne avec résolution de conflits.',
  },
  dossiers_analyses: {
    tier: 'FULL_OFFLINE',
    reason: 'Les demandes d\'analyses créées hors-ligne sont envoyées au retour réseau.',
  },
  resultats_analyses: {
    tier: 'FULL_OFFLINE',
    reason: 'Les résultats d\'analyses saisis hors-ligne sont synchronisés dès que possible.',
  },
  suivis_patients: {
    tier: 'FULL_OFFLINE',
    reason: 'Les suivis patients sont enregistrés localement puis synchronisés.',
  },
  patients: {
    tier: 'FULL_OFFLINE',
    reason: 'La création/modification de dossiers patients est possible hors-ligne.',
  },
  fiches_surveillance: {
    tier: 'FULL_OFFLINE',
    reason: 'Les fiches de surveillance sont saisissables hors-ligne.',
  },
  fiches_soins: {
    tier: 'FULL_OFFLINE',
    reason: 'Les fiches de soins infirmiers sont saisissables hors-ligne.',
  },
  bons_laboratoire: {
    tier: 'FULL_OFFLINE',
    reason: 'Les bons de laboratoire créés hors-ligne sont synchronisés au retour réseau.',
  },
  disponibilites_medecins: {
    tier: 'FULL_OFFLINE',
    reason: 'Les disponibilités peuvent être modifiées hors-ligne.',
  },
  medicaments: {
    tier: 'FULL_OFFLINE',
    reason: 'Le catalogue de médicaments est modifiable hors-ligne (rare, mais supporté).',
  },
  actualites: {
    tier: 'FULL_OFFLINE',
    reason: 'Les actualités sont modifiables hors-ligne si nécessaire.',
  },
  patient_assurances: {
    tier: 'FULL_OFFLINE',
    reason: 'Les assurances patient sont modifiables hors-ligne.',
  },

  // ========== READ ONLY OFFLINE ==========
  // Données consultables hors-ligne mais pas modifiables (risque de doublons,
  // non-idempotence, ou dépendance à un service tiers)
  file_attente: {
    tier: 'READ_ONLY_OFFLINE',
    reason: 'La file d\'attente évolue trop vite pour une queue différée fiable. Lecture seule hors-ligne.',
    offlineBlockMessage: 'La file d\'attente n\'est pas modifiable hors-ligne car l\'ordre réel en salle peut changer. Reconnectez-vous pour mettre à jour.',
  },
  video_meetings: {
    tier: 'READ_ONLY_OFFLINE',
    reason: 'La visioconférence nécessite une connexion temps réel (LiveKit/WebRTC).',
    offlineBlockMessage: 'La visioconférence nécessite une connexion internet. Veuillez vous reconnecter.',
  },
  import_patients_csv: {
    tier: 'ONLINE_ONLY',
    reason: 'L\'import en masse de patients nécessite une connexion pour éviter les doublons.',
    offlineBlockMessage: 'L\'import en masse nécessite une connexion internet. Veuillez vous reconnecter.',
  },
  import_numeros_reserves: {
    tier: 'ONLINE_ONLY',
    reason: 'L\'import de numéros réservés nécessite une connexion.',
    offlineBlockMessage: 'L\'import de numéros réservés nécessite une connexion internet.',
  },
  import_medicaments: {
    tier: 'ONLINE_ONLY',
    reason: 'L\'import de médicaments nécessite une connexion.',
    offlineBlockMessage: 'L\'import de médicaments nécessite une connexion internet.',
  },
  bulk_create_staff: {
    tier: 'ONLINE_ONLY',
    reason: 'La création en masse de comptes staff nécessite l\'envoi d\'emails/SMS.',
    offlineBlockMessage: 'La création de comptes en masse nécessite une connexion internet.',
  },
  paiement_stripe: {
    tier: 'ONLINE_ONLY',
    reason: 'Un paiement ne doit jamais être mis en file d\'attente — risque de double débit.',
    offlineBlockMessage: 'Le paiement nécessite une connexion internet sécurisée.',
  },
  otp_verification: {
    tier: 'ONLINE_ONLY',
    reason: 'La vérification OTP nécessite une connexion au serveur SMS.',
    offlineBlockMessage: 'La vérification par code nécessite une connexion internet.',
  },
  google_auth: {
    tier: 'ONLINE_ONLY',
    reason: 'La connexion Google nécessite un échange OAuth avec les serveurs Google.',
    offlineBlockMessage: 'La connexion Google nécessite une connexion internet.',
  },
  create_staff_account: {
    tier: 'ONLINE_ONLY',
    reason: 'La création de compte staff nécessite l\'envoi d\'un email d\'invitation.',
    offlineBlockMessage: 'La création de compte nécessite une connexion internet pour envoyer l\'invitation.',
  },
  upload_prescription_image: {
    tier: 'ONLINE_ONLY',
    reason: 'L\'upload d\'image nécessite une connexion au stockage Supabase.',
    offlineBlockMessage: 'L\'envoi d\'image nécessite une connexion internet.',
  },
  send_sms: {
    tier: 'ONLINE_ONLY',
    reason: 'L\'envoi de SMS nécessite une connexion au service SMS.',
    offlineBlockMessage: 'L\'envoi de SMS nécessite une connexion internet.',
  },
  send_email: {
    tier: 'ONLINE_ONLY',
    reason: 'L\'envoi d\'email nécessite une connexion au service email.',
    offlineBlockMessage: 'L\'envoi d\'email nécessite une connexion internet.',
  },
};

// ===================== MAPPING PAR TABLE SUPABASE =====================

const TABLE_TO_MODULE: Record<string, string> = {
  patients: 'patients',
  patients_temporaires: 'patients',
  rendez_vous: 'rendez_vous',
  consultations: 'consultations',
  prescriptions: 'prescriptions',
  resultats_analyses: 'resultats_analyses',
  medecins: 'medecins',
  secretaires: 'secretaires',
  laborantins: 'laborantins',
  sage_femmes: 'sage_femmes',
  infirmiers: 'infirmiers',
  comptables: 'comptables',
  medicaments: 'medicaments',
  actualites: 'actualites',
  dossiers_analyses: 'dossiers_analyses',
  suivis_patients: 'suivis_patients',
  fiches_surveillance: 'fiches_surveillance',
  fiches_soins: 'fiches_soins',
  bons_laboratoire: 'bons_laboratoire',
  disponibilites_medecins: 'disponibilites_medecins',
  dossiers_roses: 'dossiers_roses',
  file_attente: 'file_attente',
  assurances: 'assurances',
  patient_assurances: 'patient_assurances',
  notifications: 'notifications',
  messages: 'messages',
};

// ===================== API PUBLIQUE =====================

/**
 * Détermine le tier offline pour une table Supabase donnée.
 */
export function getOfflineTierForTable(tableName: string): OfflineTier {
  const moduleKey = TABLE_TO_MODULE[tableName];
  if (!moduleKey) return 'FULL_OFFLINE'; // Par défaut : full offline
  const category = MODULE_CATEGORIES[moduleKey];
  if (!category) return 'FULL_OFFLINE';
  return category.tier;
}

/**
 * Détermine le tier offline pour une opération nommée (ex: 'import_patients_csv').
 */
export function getOfflineTierForOperation(operationName: string): OperationCategory {
  return MODULE_CATEGORIES[operationName] || { tier: 'FULL_OFFLINE' };
}

/**
 * Vérifie si une opération est autorisée hors-ligne.
 * Retourne { allowed: true } ou { allowed: false, message: '...' }
 */
export function canPerformOffline(
  tableOrOperation: string,
  operationType: 'read' | 'write',
): { allowed: boolean; tier: OfflineTier; message?: string } {
  // D'abord, vérifier si c'est une opération nommée
  const namedCategory = MODULE_CATEGORIES[tableOrOperation];
  const category = namedCategory || { tier: getOfflineTierForTable(tableOrOperation) };

  if (category.tier === 'FULL_OFFLINE') {
    return { allowed: true, tier: 'FULL_OFFLINE' };
  }

  if (category.tier === 'READ_ONLY_OFFLINE') {
    if (operationType === 'read') {
      return { allowed: true, tier: 'READ_ONLY_OFFLINE' };
    }
    return {
      allowed: false,
      tier: 'READ_ONLY_OFFLINE',
      message: category.offlineBlockMessage || 'Cette action n\'est pas disponible hors-ligne. Veuillez vous reconnecter.',
    };
  }

  // ONLINE_ONLY
  return {
    allowed: false,
    tier: 'ONLINE_ONLY',
    message: category.offlineBlockMessage || 'Cette fonction nécessite une connexion internet.',
  };
}

/**
 * Vérifie si une table Supabase supporte la mutation offline complète.
 */
export function supportsOfflineMutation(tableName: string): boolean {
  return getOfflineTierForTable(tableName) === 'FULL_OFFLINE';
}

/**
 * Vérifie si une table Supabase supporte la lecture offline (via cache).
 */
export function supportsOfflineRead(tableName: string): boolean {
  const tier = getOfflineTierForTable(tableName);
  return tier === 'FULL_OFFLINE' || tier === 'READ_ONLY_OFFLINE';
}

/**
 * Liste toutes les opérations ONLINE_ONLY (pour référence).
 */
export function getOnlineOnlyOperations(): string[] {
  return Object.entries(MODULE_CATEGORIES)
    .filter(([, cat]) => cat.tier === 'ONLINE_ONLY')
    .map(([key]) => key);
}

/**
 * Liste toutes les tables FULL_OFFLINE.
 */
export function getFullOfflineTables(): string[] {
  return Object.entries(TABLE_TO_MODULE)
    .filter(([, moduleKey]) => {
      const cat = MODULE_CATEGORIES[moduleKey];
      return cat && cat.tier === 'FULL_OFFLINE';
    })
    .map(([table]) => table);
}

/**
 * Message standardisé pour bloquer une action hors-ligne.
 */
export function getOfflineBlockMessage(actionLabel: string): string {
  return `"${actionLabel}" nécessite une connexion internet. Veuillez vous reconnecter pour continuer.`;
}